import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { engineerAuthApi } from '../../services/engineerService';

const EngineerSetup: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [loadingInvite, setLoadingInvite] = useState(true);
  const [invite, setInvite] = useState<any>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [pin2, setPin2] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    engineerAuthApi
      .resolveInvite(token)
      .then((data) => setInvite(data))
      .catch((err) => setLoadError(err?.response?.data?.message || 'Invite not found or expired'))
      .finally(() => setLoadingInvite(false));
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!/^\d{6}$/.test(pin)) {
      setSubmitError('PIN must be 6 digits');
      return;
    }
    if (pin !== pin2) {
      setSubmitError('PINs do not match');
      return;
    }
    if (!token) return;
    setSubmitting(true);
    try {
      await engineerAuthApi.completeSetup(token, {
        pin,
        name: name.trim() || undefined,
        phone: phone.trim() || undefined,
      });
      navigate('/engineer/today', { replace: true });
    } catch (err: any) {
      setSubmitError(err?.response?.data?.message || 'Setup failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingInvite) {
    return (
      <div className="min-h-screen bg-[#F8F8F7] flex items-center justify-center text-[#686E6C]">
        Loading…
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-[#F8F8F7] flex items-center justify-center px-4">
        <div className="max-w-sm w-full bg-white rounded-2xl shadow-sm border border-[#E6E6E4] p-6 text-center">
          <h1 className="text-lg font-semibold text-[#181818]">Invite unavailable</h1>
          <p className="mt-2 text-sm text-[#686E6C]">{loadError}</p>
          <p className="mt-4 text-xs text-[#9CA3AF]">Ask your office to re-send the invite.</p>
        </div>
      </div>
    );
  }

  const companyName = invite?.companyName || 'Your team';
  const logoUrl: string | null = invite?.companyLogoUrl || null;
  const invitedByName: string | null = invite?.invitedByName || null;

  return (
    <div className="min-h-screen bg-[#F8F8F7] flex flex-col">
      <header className="w-full border-b border-[#E6E6E4] bg-white">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={companyName}
              className="h-8 w-auto max-w-[140px] object-contain"
            />
          ) : (
            <div className="h-8 w-8 rounded-md bg-orange-500 text-white flex items-center justify-center font-semibold text-sm">
              {companyName.slice(0, 2).toUpperCase()}
            </div>
          )}
          <div className="text-sm font-semibold text-[#181818]">{companyName}</div>
          <div className="ml-auto text-xs text-[#686E6C] hidden sm:block">
            Engineer onboarding
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-start sm:items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-sm border border-[#E6E6E4] overflow-hidden">
            <div className="px-6 py-5 border-b border-[#E6E6E4] bg-gradient-to-br from-orange-50/70 to-white">
              <div className="text-xs uppercase tracking-wide font-semibold text-orange-600">
                Welcome
              </div>
              <h1 className="text-xl font-semibold text-[#181818] mt-1">
                {invitedByName ? `${invitedByName} invited you to ${companyName}` : `Join ${companyName}`}
              </h1>
              <p className="text-sm text-[#686E6C] mt-1.5">
                Set a 6-digit PIN. You’ll use it to sign in on your phone each day.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-[#181818] mb-1.5">Email</label>
                <input
                  type="email"
                  readOnly
                  value={invite?.email || ''}
                  className="w-full rounded-lg border border-[#E6E6E4] bg-[#F8F8F7] px-3 py-2 text-sm text-[#181818]"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#181818] mb-1.5">Full name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-lg border border-[#E6E6E4] px-3 py-2 text-sm text-[#181818] focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Jane Smith"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#181818] mb-1.5">Phone (optional)</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full rounded-lg border border-[#E6E6E4] px-3 py-2 text-sm text-[#181818] focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="07700 900123"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#181818] mb-1.5">Choose a 6-digit PIN</label>
                <input
                  type="password"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full rounded-lg border border-[#E6E6E4] px-3 py-3 text-2xl tracking-[0.5em] text-center font-mono text-[#181818] focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="••••••"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#181818] mb-1.5">Confirm PIN</label>
                <input
                  type="password"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  value={pin2}
                  onChange={(e) => setPin2(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full rounded-lg border border-[#E6E6E4] px-3 py-3 text-2xl tracking-[0.5em] text-center font-mono text-[#181818] focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="••••••"
                  required
                />
              </div>

              {submitError && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                  {submitError}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-semibold py-3 rounded-lg shadow-sm"
              >
                {submitting ? 'Setting up…' : 'Set PIN & continue'}
              </button>
            </form>
          </div>

          <p className="text-xs text-[#9CA3AF] text-center mt-4">
            Your PIN is stored securely and only you know it.
          </p>
        </div>
      </main>
    </div>
  );
};

export default EngineerSetup;
